import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import InputErrors from 'components/InputErrors.jsx';
import useI18n from 'hooks/I18n/useI18n.js';
import { getCurrencySymbol } from 'modules/utils';
import {
    getPriceDecimalSeparator,
    formatPriceWithoutSymbol,
    formatPriceToAmount,
} from 'modules/utils/currency.js';

const PriceInput = ({
    className,
    name,
    label,
    value,
    currency,
    placeholder,
    isRequired,
    isDisabled,
    onChange,
    showErrors,
    clientErrors,
    serverErrors,
    inputVariant,
    locale,
    inputClassName,
}) => {
    const { trans } = useI18n();
    const [isFocus, setIsFocus] = useState(false);

    const handleChange = event => {
        const targetValue = event.target.value;

        if (targetValue.length === 0) {
            onChange(name, '', { amount: 0, currency });
            return;
        }

        const decimalSeparator = getPriceDecimalSeparator({ locale, currency });

        const validPriceRegexComat = /^[0-9]*,?[0-9]{0,2}$/;
        const validPriceRegexPoint = /^[0-9]*\.?[0-9]{0,2}$/;

        if (!validPriceRegexComat.test(targetValue) && decimalSeparator === ',') {
            return;
        }

        if (!validPriceRegexPoint.test(targetValue) && decimalSeparator === '.') {
            return;
        }

        const amount = formatPriceToAmount(targetValue);
        onChange(name, targetValue, { amount, currency });
    };

    const handleBlur = event => {
        const targetValue = event.target.value;
        const newPrice = formatPriceWithoutSymbol({ currency, locale, price: targetValue });
        const amount = formatPriceToAmount(newPrice);
        onChange(name, newPrice, { amount, currency });
        setIsFocus(false);
    };

    const handleFocus = () => setIsFocus(true);

    const hasErrors =
        showErrors &&
        ((clientErrors && clientErrors.length > 0) || (serverErrors && serverErrors.length > 0));

    return (
        <div className={classnames('textInputGroup', className)}>
            {label && (
                <label
                    htmlFor={name}
                    className={classnames({
                        'fa-input--disabled': isDisabled,
                    })}
                >
                    {label}
                    {isRequired && (
                        <span className="required-label">{trans('global.required')}</span>
                    )}
                </label>
            )}
            <div
                className={classnames('input-group', 'priceInput', inputClassName, {
                    'priceInput-focus': isFocus,
                })}
            >
                <input
                    type="text"
                    id={name}
                    name={name}
                    value={value}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    className={classnames('form-control', 'fa-input', 'fa-input--block', {
                        'fa-input--error': hasErrors,
                        'fa-input--inverse': inputVariant === 'inverse',
                        'priceInput-focus-input': isFocus,
                    })}
                    disabled={isDisabled}
                    placeholder={placeholder}
                />
                <span
                    className={classnames('input-group-addon', 'priceInput-symbol', {
                        'fa-input--error': hasErrors,
                        'priceInput-focus-symbol': isFocus,
                    })}
                >
                    {getCurrencySymbol(currency)}
                </span>
            </div>

            <InputErrors
                isVisible={showErrors}
                clientErrors={clientErrors}
                serverErrors={serverErrors}
            />
        </div>
    );
};

PriceInput.propTypes = {
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.string.isRequired,
    isRequired: PropTypes.bool,
    placeholder: PropTypes.string,
    showErrors: PropTypes.bool,
    clientErrors: PropTypes.array,
    serverErrors: PropTypes.array,
    className: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    inputVariant: PropTypes.oneOf(['inverse']),
    currency: PropTypes.string.isRequired,
    locale: PropTypes.string,
    inputClassName: PropTypes.string,
};

export default PriceInput;
